import { useEffect, useState } from 'react';
import type { Match, Poster } from '../api/types';
import { posterFileName, posterName } from '../posterName';
import ProofreadDialog from './ProofreadDialog';

const STATUS_LABEL: Record<Poster['status'], string> = {
  Draft: 'Draft',
  Pending: 'Queued',
  Generating: 'Generating',
  Ready: 'Ready',
  Failed: 'Failed',
};

async function fetchPosterFile(poster: Poster, fileName: string): Promise<File> {
  let response: Response;
  try {
    response = await fetch(poster.posterUrl!);
  } catch {
    // A cross-origin fetch that storage refuses rejects as a bare "Failed to fetch", with no
    // status to report. The poster itself is fine - it is stored and the card is showing it.
    throw new Error(
      'The browser was blocked from reading the poster image. Use "Open image" and save it from ' +
        'there. If this keeps happening the storage account needs a CORS rule for this site.',
    );
  }

  if (!response.ok) {
    throw new Error('The poster image could not be downloaded. Refresh the page and try again.');
  }

  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type || 'image/png' });
}

export default function PosterCard({
  poster,
  match,
  tournamentName,
  onRegenerate,
  onSelectVersion,
  onDelete,
}: {
  poster: Poster;
  match?: Match;
  tournamentName: string;
  onRegenerate: () => void;
  onSelectVersion: (versionId: string) => void;
  onDelete: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // Proofreading is per-session and deliberately not persisted; a regenerated poster is new wording.
  const [checkedLines, setCheckedLines] = useState<ReadonlySet<number>>(new Set());
  const [proofreadOpen, setProofreadOpen] = useState(false);
  const working = poster.status === 'Pending' || poster.status === 'Generating';
  // A draft has been saved but never sent to an image model, so it is generated rather than redone.
  const isDraft = poster.status === 'Draft';
  const proofread = poster.status === 'Ready'
    && poster.renderMode === 'FullyAiGenerated'
    && poster.expectedText.length > 0;
  const allChecked = proofread && checkedLines.size === poster.expectedText.length;
  const name = posterName(tournamentName, match);

  // Regenerating replaces the image, so anything ticked against the old one no longer applies.
  useEffect(() => {
    setCheckedLines(new Set());
  }, [poster.posterUrl]);

  const toggleLine = (index: number) =>
    setCheckedLines((current) => {
      const next = new Set(current);
      if (!next.delete(index)) next.add(index);
      return next;
    });

  const download = async () => {
    setBusy('download');
    try {
      const file = await fetchPosterFile(poster, posterFileName(name));
      const url = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'Download failed.');
    } finally {
      setBusy(null);
    }
  };

  /**
   * On a phone this opens the native share sheet with the image attached, which is the only way
   * to get a poster into Instagram or WhatsApp. Desktop browsers mostly cannot share files, so
   * there we fall back to downloading the image and copying the caption.
   */
  const share = async () => {
    setBusy('share');
    setNote(null);
    try {
      const file = await fetchPosterFile(poster, posterFileName(name));
      const payload = { files: [file], text: poster.caption ?? '', title: name };

      if (navigator.canShare?.(payload)) {
        await navigator.share(payload);
      } else {
        await download();
        await copyCaption();
        setNote('This browser cannot share files. The poster has been downloaded and the caption copied.');
      }
    } catch (error) {
      // The user dismissing the share sheet raises AbortError; that is not worth reporting.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setNote(error instanceof Error ? error.message : 'Sharing failed.');
      }
    } finally {
      setBusy(null);
    }
  };

  const copyCaption = async () => {
    if (!poster.caption) return;
    await navigator.clipboard.writeText(poster.caption);
    setNote('Caption copied to the clipboard.');
  };

  const whatsAppUrl = `https://wa.me/?text=${encodeURIComponent(poster.caption ?? '')}`;

  return (
    <article className={`card poster poster--${poster.status.toLowerCase()}`}>
      <div className={`poster__frame poster__frame--${poster.format.toLowerCase()}`}>
        {poster.posterUrl ? (
          <>
            <img src={poster.posterUrl} alt={name} loading="lazy" />
            {/* Regenerating keeps showing the old image, so without this the card looks idle. */}
            {working && (
              <div className="poster__working">
                <div className="poster__working-panel">
                  <span className="spinner" aria-hidden="true" />
                  <strong>{poster.status === 'Generating' ? 'Regenerating...' : 'Queued to regenerate...'}</strong>
                  <small>
                    This takes 20 to 60 seconds. The image below stays until the new one is ready, and
                    both are kept.
                  </small>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="poster__placeholder">
            {working ? <span className="spinner" aria-hidden="true" /> : null}
            <span>{STATUS_LABEL[poster.status]}</span>
            {isDraft && <span className="poster__hint">Not generated yet</span>}
          </div>
        )}
      </div>

      <div className="poster__body">
        <p className="pill">{STATUS_LABEL[poster.status]}</p>
        <h3>{name}</h3>
        <p className="muted small">
          {poster.provider === 'NanoBanana' ? 'Nano Banana' : 'ChatGPT Image'}
          {poster.modelUsed ? ` (${poster.modelUsed})` : poster.requestedModel ? ` (${poster.requestedModel})` : ''} &middot;{' '}
          {poster.renderMode === 'FullyAiGenerated' ? 'Fully AI generated' : 'AI art + crisp text'} &middot;{' '}
          {poster.format}
        </p>

        {poster.status === 'Failed' && <p className="banner banner--error">{poster.errorMessage}</p>}

        {proofread && (
          <>
            <button
              type="button"
              className={`proofread ${allChecked ? 'is-checked' : ''}`}
              onClick={() => setProofreadOpen(true)}
            >
              <span className="proofread__title">
                {allChecked ? 'Proofread - checked' : 'Proofread before posting'}
              </span>
              <span className="proofread__hint">
                {allChecked
                  ? 'Every line matched the image'
                  : `The model lettered this poster itself. Open the ${poster.expectedText.length} line${
                      poster.expectedText.length === 1 ? '' : 's'
                    } beside the image.`}
              </span>
            </button>

            <ProofreadDialog
              poster={poster}
              name={name}
              open={proofreadOpen}
              checkedLines={checkedLines}
              onToggleLine={toggleLine}
              onConfirmAll={() => {
                setCheckedLines(new Set(poster.expectedText.map((_, index) => index)));
                setProofreadOpen(false);
              }}
              onClose={() => setProofreadOpen(false)}
            />
          </>
        )}

        {poster.versions.length > 1 && (
          <div className="versions">
            <p className="versions__title">
              {poster.versions.length} images generated - pick the one to keep
            </p>
            <div className="versions__strip">
              {poster.versions.map((version, index) => (
                <button
                  key={version.id}
                  type="button"
                  className={`versions__item ${version.isCurrent ? 'is-current' : ''}`}
                  disabled={working || version.isCurrent}
                  title={`${version.provider === 'NanoBanana' ? 'Nano Banana' : 'ChatGPT Image'}${
                    version.modelUsed ? ` (${version.modelUsed})` : ''
                  } - ${new Date(version.createdUtc).toLocaleString('en-GB')}`}
                  onClick={() => onSelectVersion(version.id)}
                >
                  {version.url ? <img src={version.url} alt="" loading="lazy" /> : <span className="versions__missing" />}
                  <span className="versions__label">
                    {index === 0 ? 'Newest' : new Date(version.createdUtc).toLocaleDateString('en-GB')}
                    {version.isCurrent ? ' - in use' : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {poster.caption && (
          <details className="caption">
            <summary>Caption</summary>
            <pre>{poster.caption}</pre>
          </details>
        )}

        {note && <p className="muted small">{note}</p>}

        <div className="row row--wrap">
          {poster.posterUrl && (
            <>
              <button type="button" className="button button--primary" disabled={busy !== null} onClick={() => void share()}>
                {busy === 'share' ? 'Preparing...' : 'Share'}
              </button>
              <button type="button" className="button" disabled={busy !== null} onClick={() => void download()}>
                Download
              </button>
              {/* Works even when reading the blob from script is blocked - the image is just a link. */}
              <a className="button button--ghost" href={poster.posterUrl} target="_blank" rel="noreferrer">
                Open image
              </a>
              <button type="button" className="button button--ghost" onClick={() => void copyCaption()}>
                Copy caption
              </button>
              <a className="button button--ghost" href={whatsAppUrl} target="_blank" rel="noreferrer">
                WhatsApp text
              </a>
            </>
          )}
          <button
            type="button"
            className={isDraft ? 'button button--primary' : 'button button--ghost'}
            disabled={working}
            onClick={onRegenerate}
          >
            {isDraft ? 'Generate' : 'Regenerate'}
          </button>
          <button type="button" className="button button--danger" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </article>
  );
}
